import { useControlledState } from '@react-stately/utils';
import clsx from 'clsx';
import * as React from 'react';
import { useObjectRef } from 'react-aria';

import { Icon } from '@gitbook/spine-icons';

import { Button } from '../Button';
import { clipboard } from '../clipboard';
import { useFieldProps } from '../Form';
import { type IconDefinition, renderIcon } from '../util';
import { HeadlessTextInput } from './HeadlessTextInput';

export type TextInputSize = 'medium' | 'large';
export type TextInputFeedbackType = 'danger' | 'warning' | 'info' | 'success';

export type TextInputProps = Omit<React.ComponentPropsWithoutRef<'input'>, 'size' | 'prefix'> & {
    /**
     * Choose a size options for the button.
     * @default 'medium'
     */
    size?: TextInputSize;

    /**
     * Icon positioned ahead of the input.
     */
    leadingIcon?: IconDefinition | undefined;

    /**
     * Make the text input copyable using a button.
     * @default false, true if readOnly
     */
    copyable?: boolean;

    /**
     * Callback called when the copy button is pressed.
     */
    onCopy?: (event: {
        /**
         * The value that will be copied to clipboard.
         */
        value: string;

        /**
         * Allow to prevent the default behaviour and handle copying manually.
         */
        preventDefault: () => void;

        /**
         * Whether the default behaviour has been prevented.
         */
        isDefaultPrevented: boolean;
    }) => void;

    /**
     * Make the text input multiline.
     * @default false
     */
    multiline?: boolean;

    /**
     * Class name for the input element.
     */
    inputClassName?: string;

    /**
     * Visual style of the input
     * @default 'default'
     */
    kind?: 'default';

    /**
     * Prefix element to be rendered before the input.
     */
    prefix?: React.ReactNode;

    /**
     * Suffix element to be rendered after the input.
     */
    suffix?: React.ReactNode;

    /**
     * Free form trailing content rendered after the input, before suffix/copy controls.
     */
    trailing?: React.ReactNode;

    /**
     * Feedback message and type to display below the input.
     */
    feedback?: {
        type?: TextInputFeedbackType;
        message: React.ReactNode;
    };

    /**
     * Optional callback called when the clear button is pressed.
     * The clear button is only displayed if this callback is passed.
     */
    onClear?: () => void;

    /**
     * Callback called when pressing <Enter>. Disabled for multiline inputs.
     */
    onSubmit?: (
        value: string,
        event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => void;
};

export const TextInput = React.forwardRef(function TextInput(
    props: TextInputProps,
    forwardedRef: React.ForwardedRef<HTMLInputElement>
) {
    const {
        size = 'medium',
        leadingIcon,
        copyable = !!props.readOnly,
        inputClassName,
        multiline = false,
        suffix,
        prefix,
        trailing,
        feedback,
        onCopy,
        onClear,
        ...inputProps
    } = props;

    const inputRef = useObjectRef(forwardedRef);
    const { onKeyDown } = inputProps;
    const { className } = useFieldProps({
        ...inputProps,
    });

    const [value] = useControlledState(props.value, props.defaultValue ?? '');

    const canClear =
        Boolean(onClear) && Boolean(value) && !inputProps.readOnly && !inputProps.disabled;

    const feedbackTone = feedback?.type ?? null;

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (!event.defaultPrevented && event.key === 'Enter' && !event.shiftKey && !multiline) {
            inputProps.onSubmit?.(inputRef.current?.value ?? '', event);
        }

        onKeyDown?.(event as React.KeyboardEvent<HTMLInputElement>);
    };

    // biome-ignore-start lint/a11y: wrapper div is intentionally clickable to focus the input
    return (
        <>
            <div
                className={clsx(
                    'relative flex w-full cursor-text items-center gap-2 overflow-hidden text-ui-base transition',
                    'rounded-lg border border-control bg-action-secondary',
                    'px-[--input-padding-x] py-[--input-padding-y]',
                    'has-[input:disabled]:cursor-not-allowed has-[input:disabled]:bg-muted',
                    'has-[[aria-invalid=true]]:border-control-invalid',
                    feedbackTone
                        ? feedbackStylesClasses[feedbackTone]
                        : 'has-[input:focus]:border-primary has-[textarea:focus]:border-primary has-[input:focus]:shadow-focus-ring-inset has-[textarea:focus]:shadow-focus-ring-inset',
                    {
                        medium: '[--input-padding-x:0.5rem] [--input-padding-y:calc(0.375rem-1px)]',
                        large: '[--input-padding-x:0.625rem] [--input-padding-y:calc(0.5rem-1px)]',
                    }[size],
                    className
                )}
                onClick={() => inputRef.current?.focus()}
            >
                {leadingIcon
                    ? renderIcon(leadingIcon, {
                          size: 16,
                          className: 'shrink-0 text-secondary',
                      })
                    : null}

                {prefix}

                <HeadlessTextInput
                    {...inputProps}
                    ref={inputRef}
                    value={value}
                    placeholder={inputProps.placeholder ?? ''}
                    className={clsx(leadingIcon && 'pt-0.5', inputClassName)}
                    onKeyDown={handleKeyDown}
                    multiline={multiline}
                    inputClassName={
                        {
                            medium: 'text-ui-base', // h-5
                            large: 'text-ui-action', // h-5
                        }[size]
                    }
                />

                {trailing}

                {suffix}

                {copyable && (
                    <Button
                        kind="blank"
                        size="xsmall"
                        label="Copy"
                        iconOnly
                        leadingIcon={Icon.Copy}
                        onPress={() => {
                            let isDefaultPrevented = false;
                            const event = {
                                get isDefaultPrevented() {
                                    return isDefaultPrevented;
                                },
                                value: inputRef.current?.value ?? '',
                                preventDefault: () => {
                                    isDefaultPrevented = true;
                                },
                            };
                            onCopy?.(event);
                            if (!event.isDefaultPrevented) {
                                clipboard.writeText(event.value);
                            }
                        }}
                    />
                )}

                {canClear && (
                    <Button
                        kind="blank"
                        size="xsmall"
                        label="Clear"
                        iconOnly
                        leadingIcon={Icon.X}
                        onPress={() => {
                            onClear?.();
                            inputRef.current?.focus();
                        }}
                    />
                )}
            </div>

            {feedback?.message && (
                <span
                    className={clsx(
                        'text-ui-small',
                        feedbackTone ? feedbackMessageClasses[feedbackTone] : 'text-secondary'
                    )}
                >
                    {feedback.message}
                </span>
            )}
        </>
    );
    // biome-ignore-end lint/a11y: -
});

export function TextInputTextPrefix(props: { text: string }) {
    return (
        <div className="-ml-[--input-padding-x] -my-[--input-padding-y] flex h-[calc(1lh+var(--input-padding-y)*2)] max-w-[calc(100cqw-140px)] select-none items-center truncate border-r bg-side px-[--input-padding-x] text-muted">
            <span className="truncate">{props.text}</span>
        </div>
    );
}

export function TextInputTextSuffix(props: { text: string }) {
    return (
        <div className="-mr-[--input-padding-x] -my-[--input-padding-y] flex h-[calc(1lh+var(--input-padding-y)*2)] max-w-[calc(100cqw-140px)] select-none items-center truncate border-l bg-side px-[--input-padding-x] text-muted">
            <span className="truncate">{props.text}</span>
        </div>
    );
}

export type SearchInputProps = Omit<TextInputProps, 'leadingIcon' | 'type'>;

export const SearchInput = React.forwardRef(function SearchInput(
    props: SearchInputProps,
    ref: React.ForwardedRef<HTMLInputElement>
) {
    return (
        <TextInput
            ref={ref}
            type="search"
            leadingIcon={Icon.Search}
            placeholder="Search..."
            {...props}
        />
    );
});

const feedbackStylesClasses: Record<TextInputFeedbackType, string> = {
    danger: 'border-control-invalid bg-danger has-[input:focus]:shadow-focus-ring-danger-inset has-[textarea:focus]:shadow-focus-ring-danger-inset',
    warning:
        'border-hint-warning bg-warning has-[input:focus]:shadow-focus-ring-warning-inset has-[textarea:focus]:shadow-focus-ring-warning-inset',
    info: 'border-hint-info bg-info has-[input:focus]:shadow-focus-ring-inset has-[textarea:focus]:shadow-focus-ring-inset',
    success:
        'border-hint-success bg-success has-[input:focus]:shadow-focus-ring-success-inset has-[textarea:focus]:shadow-focus-ring-success-inset',
};

const feedbackMessageClasses: Record<TextInputFeedbackType, string> = {
    danger: 'text-danger',
    warning: 'text-warning',
    info: 'text-info',
    success: 'text-success',
};
